import type { ComponentChildren } from 'preact';

/**
 * Where something stands in a process of five steps, drawn like a project's stage bar (owner 2026-10-28): a new client's
 * start (agreement → first project) and an actor's way to a signed model release. Each step has its colour from the
 * projects' scale, so the bars read the same everywhere.
 */

const COLOUR = ['planning', 'editing', 'internal_review', 'with_customer', 'delivered'];

/** The bar: `done` steps filled in the colour of the step reached; `label` is the step it stands at. */
export function ProcessBar(p: { done: number; label: string; detail?: string; title?: string; of?: number }) {
  const of = p.of ?? 5;
  const at = Math.max(0, Math.min(of, p.done));
  return (
    <span class={`stagebar process-bar st-${COLOUR[Math.max(0, at - 1)]}`} role="img" aria-label={[p.label, p.detail].filter(Boolean).join(' · ')} title={p.title}>
      <span class="stagebar-segs" aria-hidden="true" style={of === 5 ? undefined : `grid-template-columns: repeat(${of}, 1fr)`}>
        {Array.from({ length: of }, (_, k) => k + 1).map((i) => (
          <span class={`stagebar-seg${i <= at ? ' is-done' : ''}`} />
        ))}
      </span>
      <span class="stagebar-label" aria-hidden="true">
        {p.label}
      </span>
      {p.detail ? (
        <span class="small muted stagebar-detail" aria-hidden="true">
          {p.detail}
        </span>
      ) : null}
    </span>
  );
}

export interface ProcessStep {
  title: string;
  done: boolean;
  /** what is done in the step, and what is missing */
  lines: { text: string; done?: boolean }[];
  /** the button for the next thing to do (shown on the first step that is not done) */
  action?: ComponentChildren;
}

/** The steps side by side: a coloured line on top when done, dashed on the step that is next. */
export function ProcessSteps(p: { steps: ProcessStep[]; label: string }) {
  const next = p.steps.findIndex((s) => !s.done);
  return (
    <ol class="process-steps" aria-label={p.label} style={`--process-n: ${p.steps.length}`}>
      {p.steps.map((s, i) => (
        <li class={`process-step st-${COLOUR[i]}${s.done ? ' is-done' : ''}${i === next ? ' is-next' : ''}`} aria-current={i === next ? 'step' : undefined}>
          <span class="process-step-title">
            {i + 1} {s.title}
          </span>
          {s.lines.map((l) => (
            <span class={`small ${l.done ? 'process-ok' : 'muted'}`}>
              {l.done ? '✓ ' : l.done === false ? '○ ' : ''}
              {l.text}
            </span>
          ))}
          {i === next && s.action ? <span class="process-action">{s.action}</span> : null}
        </li>
      ))}
    </ol>
  );
}
